import { Crown, MessageCircle, Phone } from 'lucide-react';
import { getTextColors } from './sheetStyle';
import { avatarGradient, initials } from './ShiftCardHeader';

const fmtCas = (t) => (t || '').toString().slice(0, 5);

/** Manažer první, pak podle jména — všude stejné pořadí. */
export function seraditKolegy(tym = []) {
    return [...tym].sort((a, b) => {
        if (a.manazer_smeny !== b.manazer_smeny) return a.manazer_smeny ? -1 : 1;
        return (a.jmeno || '').localeCompare(b.jmeno || '', 'cs');
    });
}

/**
 * Řádek kolegy — avatar, jméno (s korunkou u manažera směny), pozice a čas
 * směny, vpravo kulatá tlačítka zpráva a telefon. Tentýž řádek je v detailu
 * před směnou („Na směně s tebou") i v ploše Kolegové v běžící směně: detail
 * se při zahájení složí do dlaždice Kolegové a plocha ho zase rozloží.
 *
 * `onOtevrit` = klepnutí na jméno otevře kartu kolegy (číslo k přečtení,
 * e-mail, SMS) — tatáž karta jako v adresáři ve Zprávách.
 */
export default function RadekKolegy({ kolega: k, onZprava, onOtevrit, isDark }) {
    const barvy = getTextColors(isDark);
    const id = k.zamestnanec_id ?? k.id;
    const Obal = onOtevrit ? 'button' : 'span';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Obal
                {...(onOtevrit ? { type: 'button', onClick: () => onOtevrit(k), title: `Karta kolegy ${k.jmeno}` } : {})}
                style={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12,
                    background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left',
                    cursor: onOtevrit ? 'pointer' : 'default', WebkitTapHighlightColor: 'transparent',
                }}>
                <span style={{
                    position: 'relative', overflow: 'hidden',
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    background: avatarGradient(id),
                    color: '#fff', fontSize: 15, fontWeight: 800,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {initials(k.jmeno, k.prijmeni)}
                    {k.avatar_url && (
                        <img src={k.avatar_url} alt=""
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            style={{
                                position: 'absolute', inset: 0, width: '100%', height: '100%',
                                objectFit: 'cover', objectPosition: k.avatar_position || 'center',
                            }} />
                    )}
                </span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{
                        fontSize: 15, fontWeight: 700, color: barvy.textPrimary,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {k.jmeno} {k.prijmeni}
                        {k.manazer_smeny && <Crown size={13} style={{ color: '#F59E0B', flexShrink: 0 }} aria-label="Manažer směny" />}
                    </span>
                    <span style={{
                        fontSize: 12.5, color: barvy.textMuted,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {[k.pozice_nazev, (k.smena_cas_od || k.smena_cas_do)
                            ? `${fmtCas(k.smena_cas_od)}–${fmtCas(k.smena_cas_do)}` : null]
                            .filter(Boolean).join(' · ') || '—'}
                    </span>
                </span>
            </Obal>

            <AkceKolegy
                Ikona={MessageCircle} popis="Napsat zprávu" barva="#0CC0DF"
                onClick={onZprava ? () => onZprava(k) : null}
            />
            <AkceKolegy
                Ikona={Phone} popis="Zavolat" barva="#10B981"
                href={k.telefon ? `tel:${String(k.telefon).replace(/\s/g, '')}` : null}
            />
        </div>
    );
}

/** Kulaté tlačítko u kolegy — `href` z něj udělá odkaz (tel:), jinak je to akce. */
function AkceKolegy({ Ikona, popis, barva, onClick, href }) {
    if (!onClick && !href) return null;
    const Prvek = href ? 'a' : 'button';
    return (
        <Prvek
            {...(href ? { href } : { type: 'button', onClick })}
            title={popis}
            aria-label={popis}
            style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: `${barva}1F`, color: barva,
                border: `1px solid ${barva}44`,
                cursor: 'pointer', textDecoration: 'none',
                WebkitTapHighlightColor: 'transparent',
            }}>
            <Ikona size={17} />
        </Prvek>
    );
}
